type BackendAuthPayload = {
  success?: unknown
  authFailure?: unknown
  error?: unknown
}

const CREDENTIAL_FAILURE_PATTERNS = [
  /invalid_grant/i,
  /invalid token/i,
  /token is not active/i,
  /invalid or expired token/i,
  /refresh token is required/i,
]

function hasJsonContentType(response: Response): boolean {
  const contentType = response.headers.get('content-type') ?? ''
  return /^application\/(?:[a-z0-9.+-]+\+)?json(?:\s*;|$)/i.test(contentType)
}

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export async function readBackendAuthPayload<T extends object>(
  response: Response
): Promise<T | null> {
  if (!hasJsonContentType(response)) return null

  const payload = (await response.json().catch(() => null)) as unknown
  return isJsonObject(payload) ? (payload as T) : null
}

export function getBackendAuthError(
  payload: BackendAuthPayload | null
): string | undefined {
  return typeof payload?.error === 'string' && payload.error.trim()
    ? payload.error
    : undefined
}

export function isTrustedBackendAuthFailure(
  response: Response,
  payload: BackendAuthPayload | null,
  allowedStatuses: readonly number[] = [400, 401, 403, 422]
): boolean {
  if (
    response.ok ||
    !allowedStatuses.includes(response.status) ||
    payload?.success !== false
  ) {
    return false
  }

  if (payload.authFailure === true) return true

  const error = getBackendAuthError(payload)
  return Boolean(
    error && CREDENTIAL_FAILURE_PATTERNS.some(pattern => pattern.test(error))
  )
}

export function isBearerAuthenticationChallenge(response: Response): boolean {
  const challenge = response.headers.get('www-authenticate')?.trim() ?? ''
  return response.status === 401 && /^Bearer(?:\s|$)/i.test(challenge)
}
